import React, { useEffect, useState, useCallback, useRef } from "react";
import "./Quiz_Page.css";
import { Container, ProgressBar, Form, Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Common_Banner from "../common/common-banner/Common_Banner";
import {
  getQuizQuestions,
  updateAnswer,
  submitQuiz,
} from "../../features/admin/quiz/Slice";
import Data_Loader from "../common/Data_Loader/Data_Loader";
import Success_Modal from "../success-modal/Success_Modal";

const Quiz_Page = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [page, setPage] = useState(1);
  const [hasAnswered, setHasAnswered] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalTextMsg, setModalTextMsg] = useState("");

  // Timer
  const [timeLeft, setTimeLeft] = useState(null);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);

  // Fullscreen violation counter
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  
  // Warning modal states
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [isFinalWarning, setIsFinalWarning] = useState(false);
  
  // Tab switch warning modal
  const [showTabWarningModal, setShowTabWarningModal] = useState(false);
  const [tabWarningMessage, setTabWarningMessage] = useState("");
  const [isFinalTabWarning, setIsFinalTabWarning] = useState(false);
  
  // Info modal for general messages
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  
  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);

  const { currentQuestion, status, totalQuestions } = useSelector(
    (state) => state.quiz,
  );

  // Prevent multiple submit
  const submittedRef = React.useRef(false);

  // Flag to track if quiz is finished
  const isQuizFinished = useRef(false);
  
  // Flag to prevent multiple violation counts
  const isProcessingViolation = useRef(false);
  
  // Track last exit time
  const lastExitTime = useRef(0);
  
  // Refs for fullscreen handling
  const fullscreenCheckInterval = useRef(null);
  const containerRef = useRef(null);

  // Helper function to show info modal instead of alert
  const showInfo = useCallback((message) => {
    setInfoMessage(message);
    setShowInfoModal(true);
  }, []);

  // Helper function to show confirmation modal
  const showConfirmation = useCallback((message, onConfirm) => {
    setConfirmMessage(message);
    setConfirmAction(() => onConfirm);
    setShowConfirmModal(true);
  }, []);

  const showSuccessAndNavigate = useCallback(
    (textMsg, resultId) => {
      setModalTextMsg(textMsg);
      setShowSuccessModal(true);

      setTimeout(() => {
        setShowSuccessModal(false);
        navigate(`/result-page/${resultId}`);
      }, 3000);
    },
    [navigate],
  );

  // Timer init
  useEffect(() => {
    const timeLimit = currentQuestion?.user_quiz?.quiz_competition?.time_limit;
    const savedTime = localStorage.getItem(`quiz_time_${id}`);

    if (!savedTime && timeLimit) {
      setTimeLeft(Number(timeLimit) * 60);
    }
  }, [currentQuestion, id]);

  // Auto Submit
  const autoSubmit = useCallback(async () => {
    if (!id || submittedRef.current) return;

    submittedRef.current = true;
    isQuizFinished.current = true;

    if (fullscreenCheckInterval.current) {
      clearInterval(fullscreenCheckInterval.current);
    }

    showInfo("Time is over or rule violated. Quiz is being submitted.");

    try {
      await dispatch(submitQuiz({ user_quiz_id: parseInt(id) })).unwrap();
      localStorage.removeItem(`quiz_time_${id}`);
      localStorage.removeItem(`quiz_ans_${id}`);
      
      setTimeout(() => {
        navigate(`/result-page/${id}`, { replace: true });
      }, 2000);
    } catch (err) {
      console.error("Auto submit failed:", err);
    }
  }, [id, dispatch, navigate, showInfo]);

  // Load Questions
  useEffect(() => {
    if (id) {
      dispatch(getQuizQuestions({ user_quiz_id: id, page }));
    }
  }, [id, page, dispatch]);

  // Load Saved Answers
  useEffect(() => {
    const savedAnswers = localStorage.getItem(`quiz_ans_${id}`);
    if (savedAnswers) {
      setSelectedAnswers(JSON.parse(savedAnswers));
    }
  }, [id]);

  useEffect(() => {
    localStorage.setItem(`quiz_ans_${id}`, JSON.stringify(selectedAnswers));
  }, [selectedAnswers, id]);

  // Current Answer sync
  useEffect(() => {
    if (currentQuestion?.question?.id) {
      setSelectedAnswer(selectedAnswers[currentQuestion.question.id] || null);
    }
  }, [currentQuestion, selectedAnswers]);

  // Restore timer
  useEffect(() => {
    const savedTime = localStorage.getItem(`quiz_time_${id}`);
    if (savedTime) {
      setTimeLeft(parseInt(savedTime));
    }
  }, [id]);

  // Save timer
  useEffect(() => {
    if (timeLeft !== null) {
      localStorage.setItem(`quiz_time_${id}`, timeLeft);
    }
  }, [timeLeft, id]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null) return;

    const timer = setTimeout(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          (async () => {
            await autoSubmit();
          })();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, autoSubmit]);

  // Fullscreen handling functions
  const enterFullscreen = useCallback(async () => {
    if (isQuizFinished.current) return;

    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      return true;
    } catch (err) {
      console.error("Fullscreen request failed:", err);
      return false;
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  // Function to show warning modal for fullscreen
  const showWarning = useCallback((message, isFinal = false) => {
    setWarningMessage(message);
    setIsFinalWarning(isFinal);
    setShowWarningModal(true);
  }, []);

  // Function to show warning modal for tab switching
  const showTabWarning = useCallback((message, isFinal = false) => {
    setTabWarningMessage(message);
    setIsFinalTabWarning(isFinal);
    setShowTabWarningModal(true);
  }, []);

  // Handle warning modal close for fullscreen
  const handleWarningClose = useCallback(() => {
    setShowWarningModal(false);
    
    // After closing warning, try to re-enter fullscreen
    if (!isQuizFinished.current && fullscreenExitCount < 3) {
      setTimeout(() => {
        enterFullscreen();
      }, 100);
    }
  }, [enterFullscreen, fullscreenExitCount]);

  // Handle tab warning modal close
  const handleTabWarningClose = useCallback(() => {
    setShowTabWarningModal(false);
  }, []);

  // Handle fullscreen violation
  const handleFullscreenViolation = useCallback(() => {
    if (isProcessingViolation.current) return;
    
    const now = Date.now();
    if (now - lastExitTime.current < 3000) return;
    
    lastExitTime.current = now;
    isProcessingViolation.current = true;

    setFullscreenExitCount((prevCount) => {
      const newCount = prevCount + 1;
      
      if (newCount === 1) {
        showWarning(
          "⚠️ Warning: You have exited fullscreen mode. Please stay in fullscreen during the quiz. This is your first warning.",
          false
        );
      } else if (newCount === 2) {
        showWarning(
          "⚠️ FINAL WARNING: If you exit fullscreen again, your quiz will be automatically submitted.",
          true
        );
      } else if (newCount >= 3) {
        showWarning(
          "❌ You have violated the fullscreen rule 3 times. Your quiz is being auto-submitted.",
          true
        );
        setTimeout(() => {
          setShowWarningModal(false);
          autoSubmit();
        }, 2000);
        return newCount;
      }
      
      setTimeout(() => {
        isProcessingViolation.current = false;
      }, 1000);
      
      return newCount;
    });
  }, [autoSubmit, showWarning]);

  // Auto enter fullscreen when quiz starts
  useEffect(() => {
    if (currentQuestion && !isQuizFinished.current) {
      setTimeout(() => {
        enterFullscreen();
      }, 500);
    }
  }, [currentQuestion, enterFullscreen]);

  // Continuous fullscreen monitoring
  useEffect(() => {
    if (isQuizFinished.current) return;

    let lastFullscreenState = !!(document.fullscreenElement || document.webkitFullscreenElement);
    
    const enforceFullscreen = () => {
      if (isQuizFinished.current) return;
      
      const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      
      if (lastFullscreenState === true && isFullscreen === false) {
        handleFullscreenViolation();
      }
      
      lastFullscreenState = isFullscreen;
      
      if (!isFullscreen && !isProcessingViolation.current && !isQuizFinished.current) {
        enterFullscreen();
      }
    };

    fullscreenCheckInterval.current = setInterval(enforceFullscreen, 2000);
    
    const handleFullscreenChange = () => {
      if (!isQuizFinished.current) {
        const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
        if (!isFullscreen) {
          handleFullscreenViolation();
        }
      }
    };
    
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    
    return () => {
      if (fullscreenCheckInterval.current) {
        clearInterval(fullscreenCheckInterval.current);
      }
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [enterFullscreen, handleFullscreenViolation]);

  // Prevent ESC, F11, Alt+Tab, and other key combinations
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isQuizFinished.current) return;

      // Prevent ESC key
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
        
        setTimeout(() => {
          if (!isQuizFinished.current && !(document.fullscreenElement || document.webkitFullscreenElement)) {
            enterFullscreen();
          }
        }, 50);
        return false;
      }
      
      // Prevent F11 key
      if (e.key === "F11" || e.keyCode === 122) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Detect Alt key (Alt+Tab)
      if (e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        
        if (!isProcessingViolation.current) {
          setTabSwitchCount((prev) => {
            const newCount = prev + 1;
            
            if (newCount === 1) {
              showTabWarning("⚠️ Warning: Do not use Alt+Tab or switch windows during the quiz. This is your first warning.", false);
            } else if (newCount === 2) {
              showTabWarning("⚠️ FINAL WARNING: If you switch windows again, your quiz will be auto submitted.", true);
            } else if (newCount >= 3) {
              showTabWarning("❌ You have violated the window switching rule 3 times. Your quiz is being auto-submitted.", true);
              setTimeout(() => {
                setShowTabWarningModal(false);
                autoSubmit();
              }, 2000);
            }
            
            return newCount;
          });
        }
        return false;
      }
      
      // Prevent Ctrl+W, Ctrl+R, Ctrl+N, Ctrl+T
      if ((e.ctrlKey || e.metaKey) && (e.key === 'w' || e.key === 'r' || e.key === 'n' || e.key === 't')) {
        e.preventDefault();
        e.stopPropagation();
        showInfo("This action is disabled during the quiz");
        return false;
      }
      
      // Prevent F5
      if (e.key === "F5" || e.keyCode === 116) {
        e.preventDefault();
        e.stopPropagation();
        showInfo("Refreshing is disabled during the quiz");
        return false;
      }
    };

    const handleWindowBlur = () => {
      if (!isQuizFinished.current && !isProcessingViolation.current) {
        setTabSwitchCount((prev) => {
          const newCount = prev + 1;
          
          if (newCount === 1) {
            showTabWarning("⚠️ Warning: Do not switch away from the quiz window. This is your first warning.", false);
          } else if (newCount === 2) {
            showTabWarning("⚠️ FINAL WARNING: If you switch away again, your quiz will be auto submitted.", true);
          } else if (newCount >= 3) {
            showTabWarning("❌ You have violated the window switching rule 3 times. Your quiz is being auto-submitted.", true);
            setTimeout(() => {
              setShowTabWarningModal(false);
              autoSubmit();
            }, 2000);
          }
          
          return newCount;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("blur", handleWindowBlur);
    
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [enterFullscreen, autoSubmit, showInfo, showTabWarning]);

  // Right click disable
  useEffect(() => {
    const handleRightClick = (e) => {
      e.preventDefault();
      showInfo("Right Click is disabled during the quiz");
    };

    document.addEventListener("contextmenu", handleRightClick);

    return () => document.removeEventListener("contextmenu", handleRightClick);
  }, [showInfo]);

  // Copy / Paste / Selection Block
  useEffect(() => {
    const preventCopy = (e) => {
      e.preventDefault();
      showInfo("Copy is not allowed during the quiz");
    };

    const preventSelect = (e) => {
      e.preventDefault();
    };

    document.addEventListener("copy", preventCopy);
    document.addEventListener("cut", preventCopy);
    document.addEventListener("paste", preventCopy);
    document.addEventListener("selectstart", preventSelect);

    return () => {
      document.removeEventListener("copy", preventCopy);
      document.removeEventListener("cut", preventCopy);
      document.removeEventListener("paste", preventCopy);
      document.removeEventListener("selectstart", preventSelect);
    };
  }, [showInfo]);

  // Tab switch warning with visibility API
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !isQuizFinished.current) {
        setTabSwitchCount((prev) => {
          const newCount = prev + 1;

          if (newCount === 1) {
            showTabWarning("⚠️ Warning: Do not switch tabs during the quiz. This is your first warning.", false);
          } else if (newCount === 2) {
            showTabWarning("⚠️ FINAL WARNING: If you switch tab again, your quiz will be auto submitted.", true);
          } else if (newCount >= 3) {
            showTabWarning("❌ You have violated the tab switching rule 3 times. Your quiz is being auto-submitted.", true);
            setTimeout(() => {
              setShowTabWarningModal(false);
              autoSubmit();
            }, 2000);
          }

          return newCount;
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [autoSubmit, showTabWarning]);

  // Back button
  useEffect(() => {
    const handlePopState = () => {
      showConfirmation(
        "Do you want to leave the quiz? Test will be submitted.",
        async () => {
          await autoSubmit();
          navigate(`/result-page/${id}`, { replace: true });
        }
      );
    };

    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [autoSubmit, navigate, id, showConfirmation]);

  // Next button
  const handleNext = async () => {
    if (!selectedAnswer) {
      showInfo("Please select an answer");
      return;
    }

    setHasAnswered(true);

    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestion.question.id]: selectedAnswer,
    }));

    await dispatch(
      updateAnswer({
        user_quiz_id: parseInt(id),
        question_id: currentQuestion?.question?.id,
        user_answer_id: selectedAnswer,
      }),
    );

    if (page === totalQuestions) {
      showConfirmation(
        "Are you sure you want to submit quiz?",
        async () => {
          try {
            isQuizFinished.current = true;
            
            if (fullscreenCheckInterval.current) {
              clearInterval(fullscreenCheckInterval.current);
            }
            
            await dispatch(submitQuiz({ user_quiz_id: parseInt(id) })).unwrap();

            localStorage.removeItem(`quiz_time_${id}`);
            localStorage.removeItem(`quiz_ans_${id}`);

            exitFullscreen();

            showSuccessAndNavigate("Quiz submitted successfully!", id);
          } catch (err) {
            console.error("Submit failed:", err);
            showInfo("Failed to submit quiz. Please try again.");
            isQuizFinished.current = false;
          }
        }
      );
    } else {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  if (!currentQuestion) {
    return <div className="text-center mt-5">No question available</div>;
  }

  const questionText = currentQuestion?.question?.question;
  const answers = currentQuestion?.answers;
  const progress = (page / totalQuestions) * 100;

  return (
    <section className="Quiz_Page" ref={containerRef}>
      <Common_Banner title="Quiz" />

      <Container className="quiz_container">
        <div className="progress_section">
          <p>
            Question {page} / {totalQuestions}
          </p>

          <p
            style={{
              fontWeight: "bold",
              color:
                timeLeft <= 30 ? "red" : timeLeft <= 60 ? "orange" : "green",
            }}
          >
            Time Left:{" "}
            {timeLeft !== null && (
              <>
                {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:
                {String(timeLeft % 60).padStart(2, "0")}
              </>
            )}
          </p>

          <div className="progress_flex">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <ProgressBar now={progress} />
        </div>

        <div className={`quiz-card ${status === "loading" ? "loading" : ""}`}>
          <h5 className="quiz_ques">
            {page}. {questionText}
          </h5>

          <Form className="form_div">
            {answers?.map((ans) => (
              <div className="form_check_div" key={ans.id}>
                <Form.Check
                  type="radio"
                  label={ans.answer}
                  name="quiz"
                  checked={selectedAnswer === ans.id}
                  onChange={() => {
                    setSelectedAnswer(ans.id);
                    setSelectedAnswers((prev) => ({
                      ...prev,
                      [currentQuestion.question.id]: ans.id,
                    }));
                  }}
                />
              </div>
            ))}
          </Form>

          {status === "loading" && (
            <div className="overlay-loader">
              <Data_Loader />
            </div>
          )}
        </div>

        <div className="btn_group_custom">
          {page > 1 && (
            <button className="prev_btn" onClick={handlePrevious}>
              Previous
            </button>
          )}

          <button className="next_btn" onClick={handleNext}>
            {page === totalQuestions ? "Finish Quiz" : "Next"}
          </button>
        </div>
      </Container>

      {/* Fullscreen Warning Modal */}
      <Modal show={showWarningModal} onHide={handleWarningClose} centered backdrop="static" keyboard={false}>
        <Modal.Header style={{ backgroundColor: isFinalWarning ? "#dc3545" : "#ffc107", color: isFinalWarning ? "white" : "#000" }}>
          <Modal.Title>{isFinalWarning ? "Final Warning!" : "Warning!"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "16px", margin: "20px 0" }}>{warningMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={isFinalWarning ? "danger" : "warning"} onClick={handleWarningClose}>
            {fullscreenExitCount >= 3 ? "OK" : "I Understand"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tab/Window Switch Warning Modal */}
      <Modal show={showTabWarningModal} onHide={handleTabWarningClose} centered backdrop="static" keyboard={false}>
        <Modal.Header style={{ backgroundColor: isFinalTabWarning ? "#dc3545" : "#ffc107", color: isFinalTabWarning ? "white" : "#000" }}>
          <Modal.Title>{isFinalTabWarning ? "Final Warning!" : "Warning!"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "16px", margin: "20px 0" }}>{tabWarningMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={isFinalTabWarning ? "danger" : "warning"} onClick={handleTabWarningClose}>
            I Understand
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Info Modal for general messages */}
      <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} centered backdrop="static">
        <Modal.Header style={{ backgroundColor: "#17a2b8", color: "white" }}>
          <Modal.Title>Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "16px", margin: "20px 0" }}>{infoMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="info" onClick={() => setShowInfoModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered backdrop="static">
        <Modal.Header style={{ backgroundColor: "#ffc107", color: "#000" }}>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "16px", margin: "20px 0" }}>{confirmMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleConfirm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Success_Modal
        show={showSuccessModal}
        handleClose={handleCloseModal}
        text_msg={modalTextMsg}
      />
    </section>
  );
};

export default Quiz_Page;